/**
 * ProductTour
 *
 * Mounts the @reactour/tour overlay.  Must be rendered inside a <TourProvider>
 * (see App.tsx).
 *
 * Behaviour:
 *  - Auto-launches once per browser (flag persisted in localStorage via useTour).
 *  - Can be relaunched at any time by calling setIsOpen(true) from any component
 *    that consumes useTour() from @reactour/tour.
 *  - Marks the tour as seen when the user reaches the last step OR clicks Close/Skip.
 *  - Pre-navigates to the first practice area when the Bookmarks step is entered,
 *    so the Notes FAB is already mounted when the user reaches the Notes step.
 *    Navigation uses React Router's useNavigate so it is properly synchronised with
 *    the component tree (no raw history.pushState / PopStateEvent hacks).
 *  - Step indices are derived from StepKey values ("bookmarks", "notes-fab",
 *    "thank-you") so tour step order can change in the DB without breaking navigation.
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTour as useReactour } from '@reactour/tour'
import { useTour, suppressTourMarkSeenOnce } from '@/hooks/useTour'
import { useIsExpanded } from '@/hooks/useLayoutMode'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useTourSteps } from '@/hooks/useTourSteps'
import { trackEvent } from '@/services/appInsights'

// Expose the first practice-area ID to components that can't use React hooks.
declare global {
  interface Window {
    __cmmiFirstPracticeAreaId?: number
  }
}

export function ProductTour() {
  const { setIsOpen, setCurrentStep, currentStep, isOpen } = useReactour()
  const { hasSeen } = useTour()
  // The tour is gated to the `expanded` layout: below lg roughly half its anchors sit
  // inside a closed drawer or the header overflow menu, so the spotlight would
  // highlight nothing (see RESPONSIVE-DESIGN-PLAN.md section 8.1).
  const isExpandedLayout = useIsExpanded()
  const { data: bundle } = useContentBundle()
  const { data: steps = [] } = useTourSteps()
  const navigate = useNavigate()

  // Resolve special step indices by key so they are independent of sort order.
  const NOTES_FAB_STEP    = steps.findIndex(s => s.stepKey === 'notes-fab')
  const PRE_NAVIGATE_STEP = steps.findIndex(s => s.stepKey === 'bookmarks')
  const THANK_YOU_STEP    = steps.findIndex(s => s.stepKey === 'thank-you')

  // Track whether we have already issued the pre-navigation for this tour
  // session so we don't spam navigate() on every render.
  const hasPreNavigatedRef = useRef(false)
  // Track whether we have already fired the "Tour Completed" event this session.
  const hasFiredTourCompleteRef = useRef(false)
  // Track whether we have already fired the "Tour Started" event for the current open.
  const hasFiredTourStartRef = useRef(false)
  // Prevent the auto-launch from firing more than once (bundle reference may change
  // if the language is switched before the tour opens).
  const hasScheduledAutoLaunchRef = useRef(false)

  // Write the first (sorted) practice-area ID to a window global so it is
  // accessible from non-hook contexts (e.g. legacy code, tests).
  useEffect(() => {
    if (!bundle) return
    const first = bundle.practiceAreas
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)[0]
    if (first) {
      window.__cmmiFirstPracticeAreaId = first.id
    }
  }, [bundle])

  // Auto-launch on first visit, but only after the content bundle has loaded AND
  // tour steps are available. If the bundle contains no tour steps the tour is
  // simply skipped — no fallback.
  useEffect(() => {
    if (!bundle || steps.length === 0) return
    // Skip WITHOUT arming the ref: a user whose first visit is on a phone must still
    // get the auto-launch if they later open the app on a desktop. Starting and
    // immediately closing it here would burn their one launch instead.
    if (!isExpandedLayout) return
    if (hasScheduledAutoLaunchRef.current) return
    hasScheduledAutoLaunchRef.current = true
    if (!hasSeen()) {
      const timer = setTimeout(() => {
        setCurrentStep(0)
        setIsOpen(true)
      }, 800)
      return () => clearTimeout(timer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle, steps.length, isExpandedLayout])

  // Close an open tour when the viewport crosses below lg (rotation, split screen,
  // window resize). Left running, the spotlight mask keeps highlighting coordinates
  // for an element that has since moved into a drawer, which paints a stray box over
  // unrelated content.
  useEffect(() => {
    if (isExpandedLayout || !isOpen) return
    suppressTourMarkSeenOnce()
    setIsOpen(false)
  }, [isExpandedLayout, isOpen, setIsOpen])

  // Fire "Tour Started" once each time the tour opens; reset on close so
  // re-launches are tracked correctly.
  useEffect(() => {
    if (isOpen && !hasFiredTourStartRef.current) {
      hasFiredTourStartRef.current = true
      trackEvent('Tour Started')
    }
    if (!isOpen) {
      hasFiredTourStartRef.current = false
    }
  }, [isOpen])

  // Pre-navigate to the first practice area so the Notes FAB is already in the
  // DOM before the user advances to the Notes step.
  //
  // We do this in two stages:
  //   PRE_NAVIGATE_STEP (Bookmarks): navigate immediately — the user still has
  //     to click Next, giving React time to fully render the new route.
  //   NOTES_FAB_STEP (Notes FAB): safety-net for users who skip ahead.
  //     If the FAB is not yet in the DOM we navigate and poll with
  //     requestAnimationFrame until it appears, then nudge currentStep so
  //     reactour re-evaluates the highlight position.
  useEffect(() => {
    // Guard: steps may not have loaded yet or special keys may be absent.
    if (NOTES_FAB_STEP < 0 || PRE_NAVIGATE_STEP < 0 || THANK_YOU_STEP < 0) return

    const paId = window.__cmmiFirstPracticeAreaId
    if (!paId) return

    const targetPath = `/practice-area/${paId}`

    if (currentStep === THANK_YOU_STEP) {
      if (!hasFiredTourCompleteRef.current) {
        hasFiredTourCompleteRef.current = true
        trackEvent('Tour Completed')
      }
      // Final step — navigate back to the home route so the app is in a clean
      // state when the tour closes.
      if (window.location.pathname !== '/') {
        navigate('/')
      }
      return
    }

    if (currentStep === PRE_NAVIGATE_STEP) {
      // Only navigate once per tour session and only if not already there.
      if (!hasPreNavigatedRef.current && window.location.pathname !== targetPath) {
        hasPreNavigatedRef.current = true
        navigate(targetPath)
      }
    } else if (currentStep === NOTES_FAB_STEP) {
      // Safety net: user may have skipped directly to this step.
      // Navigate if needed, then poll until the FAB element is in the DOM.
      const ensureFabVisible = () => {
        if (document.querySelector('[data-tour="notes-fab"]')) {
          // FAB is mounted — nudge reactour so it re-measures the highlight.
          setCurrentStep(NOTES_FAB_STEP)
          return
        }
        // Not yet mounted — navigate (if not already on the right path) then keep polling.
        if (window.location.pathname !== targetPath) {
          navigate(targetPath)
        }
        requestAnimationFrame(ensureFabVisible)
      }

      if (!document.querySelector('[data-tour="notes-fab"]')) {
        requestAnimationFrame(ensureFabVisible)
      }
    } else {
      // Reset the pre-navigation flag whenever the user moves to a different
      // step so it fires correctly if they restart the tour.
      hasPreNavigatedRef.current = false
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, NOTES_FAB_STEP, PRE_NAVIGATE_STEP, THANK_YOU_STEP])

  return null
}

/**
 * TourContentComponent
 *
 * Replaces the default content renderer inside each tour popover.
 * Passed to <TourProvider components={{ Content: TourContentComponent }}>.
 *
 * The `content` prop is whatever was set on the StepType — either:
 *   - a plain string
 *   - a ReactElement (used by toStepType)
 */
export function TourContentComponent({ content }: { content: unknown }) {
  if (typeof content === 'string') {
    return <p style={{ margin: 0, lineHeight: 1.5 }}>{content}</p>
  }
  // ReactElement or any other renderable content — let React render it directly.
  return <>{content as React.ReactNode}</>
}
